import React, { useRef, useState, useEffect } from "react";
import { Button, Form } from "react-bootstrap";

interface GlassMagnifierProps {
  src: string;
  zoom?: number;
}

const GlassMagnifier: React.FC<GlassMagnifierProps> = ({ src, zoom = 2 }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  const [showLens, setShowLens] = useState(false);
  const [lensPosition, setLensPosition] = useState({ x: 0, y: 0 });
  const [containerHeight, setContainerHeight] = useState<number>(400);

  const [viewByLens, setViewByLens] = useState(true);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // For drag-to-pan
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);

  const lensSize = 150;
  const borderSize = 5;

  useEffect(() => {
    const updateHeight = () => {
      const drawerBody = document.querySelector(
        ".offcanvas-body"
      ) as HTMLElement;
      if (drawerBody) {
        const availableHeight = drawerBody.clientHeight - 120;
        setContainerHeight(availableHeight);
      }
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!containerRef.current) return;
    const { left, top } = containerRef.current.getBoundingClientRect();
    const x = e.clientX - left;
    const y = e.clientY - top;
    setLensPosition({ x, y });
    if (viewByLens) setShowLens(true);
  };

  const handleMouseLeave = () => {
    setShowLens(false);
  };

  const getLensOffset = () => {
    if (!containerRef.current)
      return { left: lensPosition.x + 20, top: lensPosition.y - lensSize / 2 };
    const rect = containerRef.current.getBoundingClientRect();
    const spaceRight = rect.right - (rect.left + lensPosition.x);
    const spaceLeft = lensPosition.x;
    const spaceTop = lensPosition.y;

    if (spaceRight >= lensSize + 20) {
      return { left: lensPosition.x + 20, top: lensPosition.y - lensSize / 2 };
    } else if (spaceLeft >= lensSize + 20) {
      return {
        left: lensPosition.x - lensSize - 20,
        top: lensPosition.y - lensSize / 2,
      };
    } else if (spaceTop >= lensSize + 20) {
      return {
        left: lensPosition.x - lensSize / 2,
        top: lensPosition.y - lensSize - 20,
      };
    } else {
      return { left: lensPosition.x - lensSize / 2, top: lensPosition.y + 20 };
    }
  };

  const lensOffset = getLensOffset();

  const cssSafeUrl = (url: string) => {
    return `url("${url
      .replace(/["\\]/g, "\\$&")
      .replace(/ /g, "%20")
      .replace(/\(/g, "%28")
      .replace(/\)/g, "%29")}")`;
  };

  const handleToggleView = () => {
    setViewByLens(!viewByLens);
    setShowLens(false);
    setZoomLevel(1);
  };

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 0.2, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 0.2, 1));
  };

  const handleFullscreen = () => {
    const element = containerRef.current;
    if (element && !document.fullscreenElement) {
      element.requestFullscreen();
      setIsFullscreen(true);
    } else if (document.fullscreenElement) {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Drag-to-pan handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    if (viewByLens || !containerRef.current) return;

    setIsDragging(true);
    setStartX(e.pageX - containerRef.current.offsetLeft);
    setStartY(e.pageY - containerRef.current.offsetTop);
    setScrollLeft(containerRef.current.scrollLeft);
    setScrollTop(containerRef.current.scrollTop);
  };

  const handleMouseMoveZoom = (e: React.MouseEvent) => {
    if (!isDragging || !containerRef.current) return;
    e.preventDefault();

    const x = e.pageX - containerRef.current.offsetLeft;
    const y = e.pageY - containerRef.current.offsetTop;
    const walkX = x - startX;
    const walkY = y - startY;

    containerRef.current.scrollLeft = scrollLeft - walkX;
    containerRef.current.scrollTop = scrollTop - walkY;
  };

  const handleMouseUp = () => setIsDragging(false);
  const handleMouseLeaveZoom = () => setIsDragging(false);

  return (
    <div>
      {/* Toggle + Zoom Controls */}
      <div className="d-flex align-items-center mb-3 flex-wrap gap-3">
        <div className="d-flex align-items-center">
          <span className="me-3 fw-semibold">View Mode</span>
          <span className="me-2">Lens</span>
          <Form.Check
            type="switch"
            id="view-switch"
            checked={!viewByLens}
            onChange={handleToggleView}
          />
          <span className="ms-2">Zoom</span>
        </div>

        {!viewByLens && (
          <div className="d-flex align-items-center gap-2 ms-4">
            <Button variant="secondary" onClick={handleZoomOut}>
              <span className="material-icons">zoom_out</span>
            </Button>

            <span style={{ minWidth: "40px", textAlign: "center" }}>
              {Math.round(zoomLevel * 100)}%
            </span>

            <Button variant="secondary" onClick={handleZoomIn}>
              <span className="material-icons">zoom_in</span>
            </Button>

            <Button variant="secondary" onClick={handleFullscreen}>
              <span className="material-icons">fullscreen</span>
            </Button>
          </div>
        )}
      </div>

      {/* Image Viewer */}
      <div
        ref={containerRef}
        onMouseMove={(e) => {
          handleMouseMove(e); // lens logic
          handleMouseMoveZoom(e); // zoom drag
        }}
        onMouseLeave={() => {
          handleMouseLeave(); // lens logic
          handleMouseLeaveZoom(); // zoom drag
        }}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        style={{
          width: "100%",
          height: `${containerHeight}px`,
          overflow: viewByLens ? "hidden" : "auto",
          border: "1px solid #ccc",
          background: "#000",
          position: "relative",
          cursor: viewByLens ? "default" : isDragging ? "grabbing" : "grab",
        }}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <img
            ref={imgRef}
            src={src}
            alt="zoomable"
            style={{
              transform: viewByLens ? "none" : `scale(${zoomLevel})`,
              transformOrigin: "top left",
              transition: "transform 0.3s ease-in-out",
              maxWidth: "100%",
              maxHeight: "100%",
              pointerEvents: "none",
              userSelect: "none",
            }}
          />
        </div>

        {/* Lens View */}
        {viewByLens && showLens && (
          <div
            style={{
              position: "absolute",
              pointerEvents: "none",
              border: `${borderSize}px solid rgba(255, 255, 255, 0.5)`,
              width: `350px`,
              height: `${lensSize}px`,
              left: `${lensOffset.left}px`,
              top: `${lensOffset.top}px`,
              backgroundImage: cssSafeUrl(src),
              backgroundRepeat: "no-repeat",
              backgroundSize: `${
                (containerRef.current?.offsetWidth || 0) * zoom
              }px auto`,
              backgroundPosition: `-${
                lensPosition.x * zoom - lensSize / 2
              }px -${lensPosition.y * zoom - lensSize / 2}px`,
              boxShadow: "0 0 8px rgba(0, 0, 0, 0.3)",
              zIndex: 5,
              backgroundColor: "#fff",
            }}
          />
        )}
      </div>
    </div>
  );
};

export default GlassMagnifier;
